import { createTheme, type Theme } from "@mui/material/styles";

import { resolveThemeColors, BREAKPOINTS, themeTokens, type ThemeMode } from "@config/theme/tokens";
import { formMenuItem, formMenuItemHeight } from "@config/theme/formControls";
import { themeVar } from "@config/theme/cssVar";

function buildPalette(mode: ThemeMode) {
	const colors = resolveThemeColors(mode);

	return {
		mode: "light" as const,
		primary: {
			main: colors.primary,
			dark: colors.primaryHover,
			light: colors.secondary,
			contrastText: colors.primaryForeground,
		},
		secondary: {
			main: colors.secondary,
			contrastText: colors.secondaryForeground,
		},
		error: {
			main: colors.danger,
			contrastText: colors.dangerForeground,
		},
		warning: {
			main: colors.warning,
			contrastText: colors.warningForeground,
		},
		success: {
			main: colors.success,
			contrastText: colors.successForeground,
		},
		info: {
			main: colors.info,
			contrastText: colors.infoForeground,
		},
		background: {
			default: colors.background,
			paper: colors.surface,
		},
		text: {
			primary: colors.foreground,
			secondary: colors.muted,
			disabled: colors.mutedForeground,
		},
		divider: colors.borderStrong,
	};
}

export function createMuiTheme(mode: ThemeMode = "light"): Theme {
	const { shared } = themeTokens;

	return createTheme({
		palette: buildPalette(mode),
		typography: {
			fontFamily: shared.typography.fontFamily.sans,
			fontSize: 14,
			h1: {
				fontSize: shared.typography.fontSize["3xl"],
				fontWeight: shared.typography.fontWeight.bold,
			},
			h2: {
				fontSize: shared.typography.fontSize["2xl"],
				fontWeight: shared.typography.fontWeight.semibold,
			},
			h3: {
				fontSize: shared.typography.fontSize.xl,
				fontWeight: shared.typography.fontWeight.semibold,
			},
			body1: { fontSize: shared.typography.fontSize.base },
			body2: { fontSize: shared.typography.fontSize.sm },
			button: {
				fontWeight: shared.typography.fontWeight.semibold,
				textTransform: "none",
			},
		},
		shape: {
			borderRadius: 8,
		},
		breakpoints: {
			values: {
				xs: BREAKPOINTS.xs,
				sm: BREAKPOINTS.sm,
				md: BREAKPOINTS.md,
				lg: BREAKPOINTS.lg,
				xl: BREAKPOINTS.xl,
			},
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: {
					body: {
						backgroundColor: "var(--admin-background)",
						color: "var(--admin-foreground)",
					},
				},
			},
			MuiButton: {
				defaultProps: { disableElevation: true },
				styleOverrides: {
					root: {
						textTransform: "none",
						borderRadius: "var(--admin-btn-radius, 5px)",
						fontWeight: shared.typography.fontWeight.semibold,
					},
					containedPrimary: {
						backgroundColor: "var(--admin-primary)",
						color: "var(--admin-primary-foreground)",
						"&:hover": {
							backgroundColor: "var(--admin-primary-hover)",
						},
					},
					outlinedPrimary: {
						borderColor: "var(--admin-primary)",
						color: "var(--admin-primary)",
						"&:hover": {
							backgroundColor: "var(--admin-secondary)",
							borderColor: "var(--admin-primary-hover)",
						},
					},
					containedError: {
						backgroundColor: "var(--admin-danger)",
						color: "var(--admin-danger-foreground)",
						"&:hover": {
							backgroundColor: "var(--admin-danger-hover)",
						},
					},
					outlinedError: {
						borderColor: "var(--admin-danger)",
						color: "var(--admin-danger)",
						"&:hover": {
							backgroundColor: "var(--admin-status-danger-surface)",
							borderColor: "var(--admin-danger)",
						},
					},
					textError: {
						color: "var(--admin-danger)",
						"&:hover": {
							backgroundColor: "var(--admin-status-danger-surface)",
						},
					},
				},
			},
			MuiMenuItem: {
				styleOverrides: {
					root: {
						minHeight: formMenuItemHeight,
						padding: "10px 12px",
						boxSizing: "border-box",
						"&.Mui-selected": {
							backgroundColor: formMenuItem.selectedBg,
							color: formMenuItem.selectedColor,
						},
						"&.Mui-selected:hover": {
							backgroundColor: formMenuItem.selectedHoverBg,
							color: formMenuItem.selectedColor,
						},
						"&:hover": {
							backgroundColor: formMenuItem.hoverBg,
							color: formMenuItem.hoverColor,
						},
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						borderRadius: "var(--admin-input-radius)",
						backgroundColor: "var(--admin-surface)",
						"& .MuiOutlinedInput-notchedOutline": {
							borderColor: "var(--admin-border)",
						},
						"&:hover .MuiOutlinedInput-notchedOutline": {
							borderColor: "var(--admin-border-strong)",
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							borderColor: "var(--admin-primary)",
						},
					},
				},
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						backgroundImage: "none",
						backgroundColor: "var(--admin-surface)",
					},
				},
			},
			MuiTabs: {
				styleOverrides: {
					root: {
						"& .MuiTabs-indicator": {
							backgroundColor: "var(--admin-primary)",
							height: 4,
							borderRadius: 2,
						},
					},
				},
			},
			MuiTab: {
				styleOverrides: {
					root: {
						textTransform: "capitalize",
						fontSize: 12,
						borderRadius: 8,
						minHeight: 40,
						marginRight: 8,
						transition: "background 0.2s, color 0.2s",
						"&.Mui-selected": {
							color: "var(--admin-primary-soft-text)",
							backgroundColor: "var(--admin-primary-soft)",
						},
						"&.Mui-selected:hover": {
							backgroundColor: "var(--admin-primary-soft-hover)",
						},
						"&:hover": {
							backgroundColor: "var(--admin-primary-soft-hover)",
						},
					},
				},
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						background: themeVar.foreground,
						color: themeVar.primaryForeground,
						boxShadow: "0 4px 16px rgba(0, 0, 0, 0.28), 0 1px 4px rgba(0, 0, 0, 0.18)",
						borderRadius: 6,
						fontSize: 12,
						padding: "10px 14px",
						fontFamily: shared.typography.fontFamily.sans,
					},
					arrow: {
						color: themeVar.foreground,
					},
				},
			},
			MuiCircularProgress: {
				defaultProps: {
					color: "primary",
				},
				styleOverrides: {
					root: {
						color: "var(--admin-primary)",
					},
				},
			},
			MuiLinearProgress: {
				styleOverrides: {
					root: {
						backgroundColor: "var(--admin-border)",
						borderRadius: 9999,
					},
					bar: {
						backgroundColor: "var(--admin-primary)",
						borderRadius: 9999,
					},
				},
			},
			MuiStepper: {
				styleOverrides: {
					root: {
						"& .MuiStepConnector-root": {
							"& .MuiStepConnector-line": {
								borderColor: "var(--admin-border-strong)",
							},
							"&.Mui-active .MuiStepConnector-line, &.Mui-completed .MuiStepConnector-line": {
								borderColor: "var(--admin-primary)",
							},
						},
					},
				},
			},
			MuiStepLabel: {
				styleOverrides: {
					label: {
						fontFamily: shared.typography.fontFamily.sans,
						"&.Mui-active": {
							color: "var(--admin-primary)",
							fontWeight: shared.typography.fontWeight.semibold,
						},
						"&.Mui-completed": {
							color: "var(--admin-primary)",
						},
					},
				},
			},
			MuiStepIcon: {
				styleOverrides: {
					root: {
						color: "var(--admin-border-strong)",
						"&.Mui-active": {
							color: "var(--admin-primary)",
						},
						"&.Mui-completed": {
							color: "var(--admin-primary)",
						},
					},
				},
			},
			MuiTable: {
				styleOverrides: {
					root: {
						"& .MuiTableCell-root": {
							fontFamily: shared.typography.fontFamily.sans,
						},
					},
				},
			},
		},
	});
}
